import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service - Smartasaurus" },
      { name: "description", content: "The terms that govern your use of Smartasaurus - content, accounts, newsletter, and future products." },
      { property: "og:title", content: "Terms of Service - Smartasaurus" },
      { property: "og:description", content: "Plain-English terms for using Smartasaurus." },
      { property: "og:url", content: "https://smartasaurus.com/terms" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Terms of Service - Smartasaurus" },
      { name: "twitter:description", content: "Plain-English terms for using Smartasaurus." },
    ],
    links: [{ rel: "canonical", href: "https://smartasaurus.com/terms" }],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content" className="mx-auto max-w-3xl px-4 py-16 md:px-8 md:py-20">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">· Legal</div>
        <h1 className="mt-3 font-display text-4xl uppercase leading-[1] md:text-6xl">Terms of Service</h1>
        <p className="mt-3 text-sm text-muted-foreground">Last updated: June 2026</p>

        <div className="mt-10 space-y-8 text-[15px] leading-relaxed text-muted-foreground">
          <section>
            <h2 className="font-display text-xl uppercase text-foreground">1. Who we are</h2>
            <p className="mt-2">Smartasaurus ("we", "us") is operated from Sweden. By using smartasaurus.com you agree to these Terms. If you do not agree, please don't use the site.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">2. What we offer</h2>
            <p className="mt-2">Smartasaurus publishes science-and-curiosity content, quizzes, an optional newsletter, product waitlists, and - in the future - digital and physical products. Free content is provided as-is for information and entertainment.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">3. Accounts</h2>
            <p className="mt-2">You don't need an account to read. If you create one, keep your credentials secure and accurate. You can delete your account at any time by contacting us.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">4. Newsletter & waitlists</h2>
            <p className="mt-2">Signing up means you consent to receive the relevant emails. Every email includes a one-click unsubscribe; waitlist removal can also be requested by email.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">5. Content & accuracy</h2>
            <p className="mt-2">We work to be accurate and cite sources, but Smartasaurus content is educational, not professional advice (scientific, medical, legal, or otherwise). Don't rely on it for safety-critical decisions.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">6. Intellectual property</h2>
            <p className="mt-2">Articles, images, branding, and design are owned by Smartasaurus or licensed to us. Personal sharing of links is welcome. Republishing, scraping, or training AI models on our content without written permission is not.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">7. Acceptable use</h2>
            <p className="mt-2">Don't break the site, abuse the contact form, attempt unauthorized access, submit illegal content, or impersonate others. We may suspend access if you do.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">8. Products & purchases</h2>
            <p className="mt-2">When digital or physical products launch, separate product pages will list price, delivery, and any product-specific terms. EU consumer rights and our <Link to="/refunds" className="text-primary underline-offset-4 hover:underline">refund policy</Link> apply.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">9. Liability</h2>
            <p className="mt-2">To the extent allowed by law, Smartasaurus is not liable for indirect or consequential damages from use of the site or content. Nothing in these Terms limits rights you have under mandatory consumer law.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">10. Governing law</h2>
            <p className="mt-2">These Terms are governed by Swedish law. Disputes will be handled by Swedish courts, without prejudice to mandatory consumer protection rules in your country of residence.</p>
          </section>

          <section>
            <h2 className="font-display text-xl uppercase text-foreground">11. Changes</h2>
            <p className="mt-2">We may update these Terms as the site evolves. The "Last updated" date above will reflect the latest version.</p>
          </section>

          <p className="pt-4">
            See also: <Link to="/privacy" className="text-primary underline-offset-4 hover:underline">Privacy</Link> · <Link to="/refunds" className="text-primary underline-offset-4 hover:underline">Refunds</Link> · <Link to="/contact" className="text-primary underline-offset-4 hover:underline">Contact</Link>
          </p>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
